/**
 * The storage-path conventions for the `job-photos` bucket, and the URL the app
 * serves those photos from.
 *
 * Eight namespaces share the bucket:
 *
 *   {accountId}/{jobId}/{file}                 — job photos
 *   {accountId}/gas-certs/{certId}/{file}      — gas certificate evidence
 *   {accountId}/access-attempts/{jobId}/{file} — the photo taken at a door that would not open
 *   {accountId}/project-{projectId}/{file}     — project photos
 *   {accountId}/plot-{propertyId}/{file}       — photos put straight on a PLOT (TKT-1338)
 *   {accountId}/quote-photos/{file}            — photos put on a quote
 *   {accountId}/appointment-{apptId}/{file}    — photos on a diary appointment (TKT-1359)
 *   {accountId}/surveys/{surveyId}/{file}      — installation survey photos
 *
 * 🚨 An access-attempt photo is in its OWN namespace rather than the job's, and
 * the reason is deletion. `DELETE /api/jobs/[id]/photos` exists and the job
 * photo grid renders a ✕ on every tile; a photograph proving an engineer stood
 * at a door on a Tuesday is evidence a landlord may need in eleven months, and
 * evidence somebody can remove with a mis-tap is not evidence. Filed here it is
 * never listed by the job photo GET and never reachable by that DELETE, which
 * matches the append-only rule on `job_access_attempts` itself.
 *
 * The first, fourth, fifth, sixth and seventh are all three segments, told
 * apart by the `project-` / `plot-` / `appointment-` prefixes and the fixed
 * `quote-photos` segment. Job ids
 * are UUIDs, so a job id can never be any of them and the shapes can never be
 * confused.
 *
 * 🚨 The plot namespace holds only what the OFFICE puts straight on a plot from
 * its page. An engineer's photo of plot 4 is taken on his JOB and stays in the
 * job's folder — his seat cannot open a plot — and `plot_photos` is what says
 * it is plot 4's. So "the photos of a plot" is never a listing of this folder;
 * it is a read of that table. See docs/per-plot-timeline-plan.md.
 *
 * 🚨 The quote namespace is NOT keyed by quote id, and that is deliberate. A
 * photograph is chosen while the quote is still a draft with no id — the id
 * only exists once it has been saved — so a folder named after it could only
 * be written to after the fact, which would mean holding files in the browser
 * and orchestrating an upload through four different save paths.
 *
 * It costs nothing, because the folder was never the gate. What authorises a
 * customer to see a picture is that THIS QUOTE stores THAT PATH
 * (`quotePhotoPathsFor`), read back off the quote's own row — a check a folder
 * name cannot make, since it would still serve a photograph the owner removed
 * from the quote last week.
 *
 * 🚨 The namespace holds COPIES, not references. A photograph picked off a job
 * onto a quote is copied here rather than pointed at, so deleting the job
 * photo next month cannot blank a quote a customer is still holding — and so
 * the gate on a quote photo is the quote's, not the job's.
 *
 * This module is imported by CLIENT components (CertPhotoSlot), so it must stay
 * free of node-only imports.
 */

export type JobPhotoRef =
  | { kind: 'job'; accountId: string; jobId: string; filename: string }
  | { kind: 'gas-cert'; accountId: string; certId: string; filename: string }
  | { kind: 'access-attempt'; accountId: string; jobId: string; filename: string }
  | { kind: 'project'; accountId: string; projectId: string; filename: string }
  | { kind: 'plot'; accountId: string; propertyId: string; filename: string }
  | { kind: 'quote'; accountId: string; filename: string }
  | { kind: 'appointment'; accountId: string; appointmentId: string; filename: string }
  /**
   * A survey's own folder, because a survey can exist before any job does — it
   * is often what decides whether there will be one. The filename is the
   * photo's uuid, minted on the phone, so a retried offline upload overwrites
   * itself instead of filing the same picture twice.
   */
  | { kind: 'survey'; accountId: string; surveyId: string; filename: string };

const PROJECT_PREFIX = 'project-';
const PLOT_PREFIX = 'plot-';
const APPOINTMENT_PREFIX = 'appointment-';
const QUOTE_PHOTOS_SEGMENT = 'quote-photos';
const GAS_CERTS_SEGMENT = 'gas-certs';
const ACCESS_ATTEMPTS_SEGMENT = 'access-attempts';
const SURVEYS_SEGMENT = 'surveys';

/**
 * A stored filename segment.
 *
 * Deliberately looser than the equivalent in job-attachment-paths.ts. Upload
 * routes build these as `${randomUUID()}.${ext}` where `ext` came from the
 * client's filename and was never validated, so objects already in the bucket
 * may carry an extension we would not generate today. This has to admit them —
 * a stricter rule would 404 historical photos, which is a data-loss-shaped bug
 * for something as legally-loaded as gas certificate evidence.
 *
 * What it does enforce is the part that matters: no separator, so a filename
 * can never grow the path an extra segment and escape its namespace.
 */
function isSafeFilename(name: string): boolean {
  return name.length > 0 && name !== '.' && name !== '..' && !name.includes('/') && !name.includes('\\');
}

/**
 * Parse an untrusted storage path into the entity whose permissions govern it.
 *
 * Returns null for anything that is not one of the three known shapes. The
 * caller must still check the parsed `accountId` against the resolved session —
 * this function establishes SHAPE, not ownership, and cannot do otherwise
 * because it runs on the client too.
 */
export function parseJobPhotoPath(path: string): JobPhotoRef | null {
  if (typeof path !== 'string' || !path) return null;
  // Structural rejections before splitting: a leading slash, a traversal
  // segment or a doubled separator are all outright malformed here.
  if (path.startsWith('/') || path.includes('..') || path.includes('//')) return null;

  const segments = path.split('/');

  if (segments.length === 4) {
    const [accountId, marker, entityId, filename] = segments;
    if (!accountId || !entityId || !isSafeFilename(filename)) return null;
    if (marker === GAS_CERTS_SEGMENT) {
      return { kind: 'gas-cert', accountId, certId: entityId, filename };
    }
    if (marker === ACCESS_ATTEMPTS_SEGMENT) {
      return { kind: 'access-attempt', accountId, jobId: entityId, filename };
    }
    if (marker === SURVEYS_SEGMENT) {
      return { kind: 'survey', accountId, surveyId: entityId, filename };
    }
    return null;
  }

  if (segments.length === 3) {
    const [accountId, entity, filename] = segments;
    if (!accountId || !entity || !isSafeFilename(filename)) return null;

    if (entity.startsWith(PROJECT_PREFIX)) {
      const projectId = entity.slice(PROJECT_PREFIX.length);
      if (!projectId) return null;
      return { kind: 'project', accountId, projectId, filename };
    }
    if (entity.startsWith(PLOT_PREFIX)) {
      const propertyId = entity.slice(PLOT_PREFIX.length);
      if (!propertyId) return null;
      return { kind: 'plot', accountId, propertyId, filename };
    }
    if (entity.startsWith(APPOINTMENT_PREFIX)) {
      const appointmentId = entity.slice(APPOINTMENT_PREFIX.length);
      if (!appointmentId) return null;
      return { kind: 'appointment', accountId, appointmentId, filename };
    }
    if (entity === QUOTE_PHOTOS_SEGMENT) {
      return { kind: 'quote', accountId, filename };
    }
    // A path shaped like a job photo whose second segment is a namespace marker
    // would be that folder with no entity id — not a valid object either way.
    if (entity === GAS_CERTS_SEGMENT || entity === ACCESS_ATTEMPTS_SEGMENT || entity === SURVEYS_SEGMENT) return null;
    return { kind: 'job', accountId, jobId: entity, filename };
  }

  return null;
}

/**
 * Where a diary appointment's photographs live — the tip ticket from a dump
 * run, the slip from the merchant (TKT-1359). A plain folder, not a table:
 * the photos belong to exactly one appointment and are listed by folder, the
 * way a project's are.
 */
export function appointmentPhotoFolder(accountId: string, appointmentId: string): string {
  return `${accountId}/${APPOINTMENT_PREFIX}${appointmentId}`;
}

/** Where a survey's photographs live. */
export function surveyPhotoFolder(accountId: string, surveyId: string): string {
  return `${accountId}/${SURVEYS_SEGMENT}/${surveyId}`;
}

/** Where a job's access-attempt photographs live. */
export function accessAttemptPhotoFolder(accountId: string, jobId: string): string {
  return `${accountId}/${ACCESS_ATTEMPTS_SEGMENT}/${jobId}`;
}

/** The folder an account's quote photographs live in. */
export function quotePhotoFolder(accountId: string): string {
  return `${accountId}/${QUOTE_PHOTOS_SEGMENT}`;
}

/** Where the photographs put straight on a plot from its page live. */
export function plotPhotoFolder(accountId: string, propertyId: string): string {
  return `${accountId}/${PLOT_PREFIX}${propertyId}`;
}

/**
 * Where a job-photos object's bytes are served from.
 *
 * An app route rather than a storage URL, for the same reason chat attachments
 * moved: `job-photos` holds gas certificate evidence and photos taken inside
 * customers' homes, and a public storage URL keeps working for anyone it is
 * forwarded to, with no login and no expiry.
 *
 * Chosen over a signed URL specifically so this stays SYNCHRONOUS. The cert
 * form resolves paths to URLs inside a render (`PhotoEvidence.resolveUrl`), and
 * `createSignedUrl` is async — making it async would have meant reshaping that
 * component and every caller. A same-origin route carries the session cookie on
 * a plain `<img src>`, so nothing above it has to change.
 */
export function jobPhotoUrl(path: string): string {
  return `/api/job-photos?path=${encodeURIComponent(path)}`;
}

/**
 * Where a stored object's grid thumbnail lives: a hidden `.thumbs/` folder
 * beside the original, keyed by the original's full filename plus `.jpg`.
 *
 *   {accountId}/{jobId}/{file}                  → {accountId}/{jobId}/.thumbs/{file}.jpg
 *   {accountId}/gas-certs/{certId}/{file}       → …/gas-certs/{certId}/.thumbs/{file}.jpg
 *   {accountId}/project-{projectId}/{file}      → …/project-{projectId}/.thumbs/{file}.jpg
 *
 * Chosen so that:
 *  - either path is computable from the other with pure string ops — no DB
 *    column, no lookup (`originalPathForThumbnail` is the inverse);
 *  - it CANNOT collide with a real upload: filenames are single segments
 *    (`safeExtension`/`isSafeFilename` refuse separators), so no upload can
 *    ever create a `.thumbs/` sub-path;
 *  - `parseJobPhotoPath` rejects every thumbnail path (wrong segment count or
 *    marker), so the serving route can never be pointed at a thumbnail
 *    directly — thumbnails are only reachable via `?thumb=1` on the original,
 *    which keeps the permission gates keyed to one path shape;
 *  - the dot prefix makes the folder invisible to every existing listing site
 *    for free — they all filter `!name.startsWith('.')` already.
 *
 * The appended `.jpg` keeps the name honest (thumbnails are always JPEG,
 * whatever the original was) while keeping the inverse trivial.
 */
const THUMBS_SEGMENT = '.thumbs';

export function thumbnailPath(path: string): string {
  const cut = path.lastIndexOf('/');
  return `${path.slice(0, cut)}/${THUMBS_SEGMENT}${path.slice(cut)}.jpg`;
}

/** Inverse of `thumbnailPath`. Null when the path isn't a thumbnail. */
export function originalPathForThumbnail(thumbPath: string): string | null {
  const marker = `/${THUMBS_SEGMENT}/`;
  const at = thumbPath.lastIndexOf(marker);
  if (at < 0 || !thumbPath.endsWith('.jpg')) return null;
  return thumbPath.slice(0, at) + thumbPath.slice(at + marker.length - 1, -'.jpg'.length);
}

/**
 * The grid-sized rendition of a photo. Same authenticated route, so the same
 * permission gates apply; the route falls back to the original bytes when no
 * thumbnail exists (pre-backfill objects) or the object is a video.
 */
export function jobPhotoThumbUrl(path: string): string {
  return `${jobPhotoUrl(path)}&thumb=1`;
}

/**
 * Sanitise a file extension taken from a client-supplied filename.
 *
 * The upload routes build `${randomUUID()}.${ext}` from
 * `file.name.split('.').pop()`, which was never checked. A file named
 * `x.jpg/../../evil` yields an `ext` containing separators, so the "filename"
 * grows extra path segments. It stays inside the caller's own account/job
 * prefix so it was never cross-tenant, but it let a caller write outside the
 * folder the delete and sweep paths expect to find things in.
 */
export function safeExtension(filename: string | undefined | null, fallback = 'jpg'): string {
  const raw = (filename || '').split('.').pop()?.toLowerCase() ?? '';
  return /^[a-z0-9]{1,5}$/.test(raw) ? raw : fallback;
}

/**
 * The extension a stored object gets, from its VERIFIED content type.
 *
 * 🚨 The type, not the client's filename. The extension is what
 * `mediaKindForName` reads back off the bucket listing, so an unnamed clip
 * falling through to `.jpg` would hide it from the player — and a `.heic` an
 * iPhone uploaded would keep a name that renders in nothing but Safari, after
 * `normaliseUploadedImage` has already turned the bytes into a JPEG.
 *
 * This lived in THREE route files, copied. They had already diverged: the job
 * route grew the video branches and the gas-cert and project routes never did.
 * Nothing was broken by it yet, which is the only reason it survived —
 * "two ways to write one thing" is the shape that lost `no_invoice_needed`,
 * then `project_id`, then flattened fourteen invoice lines to `labour`.
 */
export function extensionForStored(mimeType: string, originalName?: string | null): string {
  const subtype = mimeType.split('/')[1]?.toLowerCase();
  if (subtype === 'jpeg' || subtype === 'jpg') return 'jpeg';
  if (subtype === 'png' || subtype === 'webp' || subtype === 'gif') return subtype;
  if (mimeType === 'video/mp4') return 'mp4';
  if (mimeType === 'video/quicktime') return 'mov';
  if (mimeType === 'video/webm') return 'webm';
  return safeExtension(originalName);
}

/** Extensions the bucket holds that are video, not stills. */
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'webm', 'm4v', 'avi', 'mkv', '3gp', 'mpeg', 'mpg']);

/**
 * Is this stored object a video?
 *
 * Decided from the extension because the bucket listing is all the reader has —
 * Supabase's `list()` returns names, not content types, and the objects that
 * need this most are the ones the Commusoft importer wrote, which nothing in
 * the app ever labelled. Job media was assumed to be photos throughout, so
 * every imported video rendered as a broken `<img>` on the job screen.
 *
 * The list is deliberately wider than what we accept on upload: it has to
 * recognise whatever a customer's old system happened to store.
 */
export function mediaKindForName(filename: string): 'image' | 'video' {
  const ext = (filename || '').split('.').pop()?.toLowerCase() ?? '';
  return VIDEO_EXTENSIONS.has(ext) ? 'video' : 'image';
}

/**
 * Make sure a stored filename ends in something `mediaKindForName` will call a
 * video, adding an extension from the content type when it does not.
 *
 * Lives HERE, next to the set it has to agree with, rather than at the call
 * site. The two answers are the same question asked in opposite directions —
 * "what will this name be read as" and "what must I name it to be read that
 * way" — and a copy of the list somewhere else is a copy that drifts.
 *
 * The reason it is needed at all: WhatsApp hands over a clip with no filename
 * of its own, and the job's Photos section decides image-vs-video from the
 * NAME, not from any `kind` column. A clip stored as `whatsapp-1757800000`
 * renders as a broken <img> on the job, with nothing anywhere saying why.
 */
export function ensureVideoExtension(filename: string, mimeType: string): string {
  const ext = (filename || '').split('.').pop()?.toLowerCase() ?? '';
  if (VIDEO_EXTENSIONS.has(ext)) return filename;

  const fromMime = mimeType === 'video/quicktime' ? 'mov'
    : mimeType === 'video/webm' ? 'webm'
    : mimeType === 'video/3gpp' ? '3gp'
    // Everything else that reaches here is an MP4 variant far more often than
    // it is anything else, and the extension only has to be honest enough for
    // the reader above to route it to a <video>.
    : 'mp4';

  const base = filename && filename.trim() ? filename : `video-${Date.now()}`;
  return `${base}.${fromMime}`;
}
